import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Avatar,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();
const API_ORIGIN = API_BASE_URL === '/api' ? '' : API_BASE_URL.replace(/\/api\/?$/, '');
const ALLOWED_ATTENDANCE_STATUSES = ['present', 'absent', 'late', 'excused'];

const normalizeStatus = (value) => {
  const status = (value || '').toString().trim().toLowerCase();
  if (ALLOWED_ATTENDANCE_STATUSES.includes(status)) {
    return status;
  }
  return '';
};

const AdminAttendance = () => {
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [changes, setChanges] = useState({}); // Track unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [dueOnly, setDueOnly] = useState(false);
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [joinFrom, setJoinFrom] = useState('');
  const [joinTo, setJoinTo] = useState('');
  const [sortBy, setSortBy] = useState('name_asc');
  const [courseOptions, setCourseOptions] = useState([]);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappTemplate, setWhatsappTemplate] = useState(
    'Dear Parent/Student,\n\n{name} was marked ABSENT on {date}.\nPlease ensure regular attendance at KNLS.\nIf there is any issue, please contact the center.\n\n- KNLS Team'
  );

  useEffect(() => {
    // Only load attendance if authenticated
    // ProtectedRoute handles redirect, but we check here to avoid unnecessary API calls
    if (isAuthenticated) {
      loadAttendance();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, isAuthenticated, searchTerm, courseFilter, dueOnly, attendanceStatusFilter, joinFrom, joinTo, sortBy]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCourseOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadCourseOptions = async () => {
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_BASE_URL}/students/courses/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data?.success) {
        setCourseOptions(response.data.courses || []);
      }
    } catch (error) {
      console.warn('Failed to load course options for attendance filters:', error?.response?.data || error.message);
    }
  };

  const loadAttendance = async () => {
    try {
      setLoading(true);
      
      // Get token from localStorage (admin_token for admin routes)
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      
      console.log('📅 [FRONTEND] Loading attendance for date:', selectedDate);
      console.log('📅 [FRONTEND] API URL:', `${API_BASE_URL}/students/attendance/${selectedDate}`);
      console.log('📅 [FRONTEND] Token exists:', !!token);
      
      if (!token) {
        console.error('❌ [FRONTEND] No token found!');
        toast.error('Please login to access attendance');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_BASE_URL}/students/attendance/${selectedDate}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          search: searchTerm || undefined,
          course: courseFilter !== 'all' ? courseFilter : undefined,
          dueOnly: dueOnly ? 'true' : undefined,
          attendanceStatus: attendanceStatusFilter !== 'all' ? attendanceStatusFilter : undefined,
          joinFrom: joinFrom || undefined,
          joinTo: joinTo || undefined,
          sortBy,
        },
      });

      console.log('📅 [FRONTEND] Response:', response.data);

      if (response.data.success) {
        console.log('📅 [FRONTEND] Attendance data:', response.data.attendance);
        console.log('📅 [FRONTEND] Total students:', response.data.attendance?.length);
        
        setAttendanceData(response.data.attendance || []);
        setSummary(response.data.summary);
        setChanges({});
        setHasChanges(false);
        
        if (!response.data.attendance || response.data.attendance.length === 0) {
          console.warn('⚠️ [FRONTEND] No students returned from API');
          toast.warning('No students found. Please check if students are registered.');
        }
      } else {
        console.error('❌ [FRONTEND] API returned success:false', response.data);
        toast.error(response.data.message || 'Failed to load attendance');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error loading attendance:', error);
      console.error('❌ [FRONTEND] Error response:', error.response?.data);
      console.error('❌ [FRONTEND] Error status:', error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to access attendance.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to load attendance');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckInTimeChange = (studentId, time) => {
    const newChanges = { ...changes };
    if (!newChanges[studentId]) {
      newChanges[studentId] = {};
    }
    newChanges[studentId].checkInTime = time;
    
    // Auto-enable present checkbox if check-in time is entered
    if (time && !newChanges[studentId].status) {
      newChanges[studentId].status = 'present';
    }
    
    setChanges(newChanges);
    setHasChanges(true);
  };

  const handleCheckOutTimeChange = (studentId, time) => {
    const newChanges = { ...changes };
    if (!newChanges[studentId]) {
      newChanges[studentId] = {};
    }
    newChanges[studentId].checkOutTime = time;
    
    // Auto-enable present checkbox if check-out time is entered
    if (time && !newChanges[studentId].status) {
      newChanges[studentId].status = 'present';
    }
    
    setChanges(newChanges);
    setHasChanges(true);
  };

  const handleStatusChange = (studentId, status) => {
    const newChanges = { ...changes };
    if (!newChanges[studentId]) {
      newChanges[studentId] = {};
    }
    newChanges[studentId].status = status;
    
    // Clear times if status is absent
    if (status === 'absent') {
      newChanges[studentId].checkInTime = '';
      newChanges[studentId].checkOutTime = '';
    }
    
    setChanges(newChanges);
    setHasChanges(true);
  };

  const handlePresentCheckboxChange = (studentId, checked) => {
    handleStatusChange(studentId, checked ? 'present' : '');
  };

  const handleRemarksChange = (studentId, remarks) => {
    const newChanges = { ...changes };
    if (!newChanges[studentId]) {
      newChanges[studentId] = {};
    }
    newChanges[studentId].remarks = remarks;
    setChanges(newChanges);
    setHasChanges(true);
  };

  const getCurrentValue = (studentId, field) => {
    if (changes[studentId] && changes[studentId][field] !== undefined) {
      return changes[studentId][field];
    }
    const student = attendanceData.find(s => s.studentId === studentId);
    if (field === 'status') {
      return normalizeStatus(student?.attendance?.status);
    }
    if (field === 'checkInTime') {
      const value = student?.attendance?.checkInTime;
      return value ? value : '';
    }
    if (field === 'checkOutTime') {
      const value = student?.attendance?.checkOutTime;
      return value ? value : '';
    }
    if (field === 'remarks') {
      const value = student?.attendance?.remarks;
      return value ? value : '';
    }
    return '';
  };

  const isPresent = (studentId) => {
    return normalizeStatus(getCurrentValue(studentId, 'status')) === 'present';
  };

  const getStatusRowStyles = (status) => {
    if (status === 'present') {
      return {
        bg: 'rgba(76, 175, 80, 0.10)',
        hover: 'rgba(76, 175, 80, 0.18)',
        borderColor: 'success.main',
      };
    }
    if (status === 'absent') {
      return {
        bg: 'rgba(244, 67, 54, 0.10)',
        hover: 'rgba(244, 67, 54, 0.18)',
        borderColor: 'error.main',
      };
    }
    if (status === 'late') {
      return {
        bg: 'rgba(255, 152, 0, 0.12)',
        hover: 'rgba(255, 152, 0, 0.20)',
        borderColor: 'warning.main',
      };
    }
    if (status === 'excused') {
      return {
        bg: 'rgba(33, 150, 243, 0.10)',
        hover: 'rgba(33, 150, 243, 0.18)',
        borderColor: 'info.main',
      };
    }
    return {
      bg: 'transparent',
      hover: 'rgba(0,0,0,0.05)',
      borderColor: 'divider',
    };
  };

  const getDynamicSummary = () => {
    const totals = {
      total: attendanceData.length,
      present: 0,
      absent: 0,
      late: 0,
      notMarked: 0,
    };

    attendanceData.forEach((student) => {
      const status = normalizeStatus(getCurrentValue(student.studentId, 'status'));
      if (status === 'present') {
        totals.present += 1;
      } else if (status === 'absent') {
        totals.absent += 1;
      } else if (status === 'late') {
        totals.late += 1;
      } else {
        totals.notMarked += 1;
      }
    });

    return totals;
  };

  const saveAttendance = async () => {
    try {
      setSaving(true);

      // Get token from localStorage
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      
      if (!token) {
        toast.error('Please login to save attendance');
        setSaving(false);
        return;
      }

      // Check if there are any students
      if (!attendanceData || attendanceData.length === 0) {
        toast.error('No students found. Please add students first.');
        setSaving(false);
        return;
      }

      // Check if there are any changes to save
      if (!hasChanges) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      // Save only changed students so one click updates only that student.
      const attendanceRecords = Object.entries(changes).map(([studentId, studentChanges]) => {
        const student = attendanceData.find(s => s.studentId === studentId) || {};

        const checkInTime = studentChanges.checkInTime !== undefined
          ? (studentChanges.checkInTime === '' ? null : studentChanges.checkInTime)
          : (student.attendance?.checkInTime || null);
        const checkOutTime = studentChanges.checkOutTime !== undefined
          ? (studentChanges.checkOutTime === '' ? null : studentChanges.checkOutTime)
          : (student.attendance?.checkOutTime || null);
        const remarks = studentChanges.remarks !== undefined
          ? (studentChanges.remarks === '' ? null : studentChanges.remarks)
          : (student.attendance?.remarks || null);

        return {
          studentId,
          status: normalizeStatus(
            studentChanges.status !== undefined
              ? studentChanges.status
              : student.attendance?.status
          ),
          checkInTime,
          checkOutTime,
          remarks,
        };
      });

      console.log('💾 [FRONTEND] Saving attendance records:', {
        date: selectedDate,
        totalRecords: attendanceRecords.length,
        sampleRecord: attendanceRecords[0],
        apiUrl: `${API_BASE_URL}/students/attendance/mark`,
        hasToken: !!token,
        tokenLength: token?.length,
      });

      const requestPayload = {
        date: selectedDate,
        attendanceRecords,
      };

      console.log('💾 [FRONTEND] Request payload:', JSON.stringify(requestPayload, null, 2));

      const response = await axios.post(
        `${API_BASE_URL}/students/attendance/mark`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        console.log('✅ [FRONTEND] Save successful:', response.data.results);
        toast.success(`Attendance saved: ${response.data.results.created} created, ${response.data.results.updated} updated`);
        setChanges({});
        setHasChanges(false);
        // Small delay to ensure backend has processed the save
        setTimeout(() => {
          console.log('🔄 [FRONTEND] Reloading attendance after save...');
          loadAttendance(); // Reload to get updated data
        }, 100);
      } else {
        console.error('❌ [FRONTEND] Save failed:', response.data);
        toast.error(response.data.message || 'Failed to save attendance');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Error saving attendance:', error);
      console.error('❌ [FRONTEND] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        },
      });
      
      let errorMessage = 'Failed to save attendance';
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status} ${error.response.statusText}`;
        if (error.response.data?.details) {
          console.error('❌ [FRONTEND] Server error details:', error.response.data.details);
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check if the backend server is running on port 3000.';
        console.error('❌ [FRONTEND] No response received. Is backend server running?');
      } else {
        // Something else happened
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCourseFilter('all');
    setDueOnly(false);
    setAttendanceStatusFilter('all');
    setJoinFrom('');
    setJoinTo('');
    setSortBy('name_asc');
  };

  const getCourseLabel = () => {
    if (courseFilter === 'all') return 'All Courses';
    const selected = courseOptions.find((course) => course.course_code === courseFilter);
    return selected?.course_name || courseFilter;
  };

  const getFilteredStats = () => {
    const stats = {
      filteredTotal: attendanceData.length,
      filteredPresent: 0,
      filteredAbsent: 0,
      filteredLate: 0,
      filteredNotMarked: 0,
      filteredDue: 0,
      filteredJoinedRangeCount: 0,
      filteredWhatsappReachable: 0,
    };

    attendanceData.forEach((student) => {
      const status = normalizeStatus(getCurrentValue(student.studentId, 'status'));
      const dueFee = Number(student?.due_fee || 0);
      const mobile = (student.whatsappNumber || student.phone || '').toString().replace(/\D/g, '');

      if (status === 'present') {
        stats.filteredPresent += 1;
      } else if (status === 'absent') {
        stats.filteredAbsent += 1;
      } else if (status === 'late') {
        stats.filteredLate += 1;
      } else {
        stats.filteredNotMarked += 1;
      }

      if (dueFee > 0) {
        stats.filteredDue += 1;
      }

      if (mobile.length >= 10) {
        stats.filteredWhatsappReachable += 1;
      }

      if (joinFrom || joinTo) {
        stats.filteredJoinedRangeCount += 1;
      }
    });

    return stats;
  };

  const getAbsentStudentsForWhatsapp = () =>
    attendanceData.filter((student) => {
      const currentStatus = normalizeStatus(getCurrentValue(student.studentId, 'status'));
      const mobile = (student.whatsappNumber || student.phone || '').toString().replace(/\D/g, '');
      return currentStatus === 'absent' && mobile.length >= 10;
    });

  const sendAbsentWhatsappMessages = () => {
    const absentStudents = getAbsentStudentsForWhatsapp();
    if (absentStudents.length === 0) {
      toast.info('No absent students with valid WhatsApp number in current filtered list.');
      return;
    }

    absentStudents.forEach((student, index) => {
      const rawMobile = (student.whatsappNumber || student.phone || '').toString().replace(/\D/g, '');
      const mobile = rawMobile.length > 10 ? rawMobile.slice(-10) : rawMobile;
      const message = whatsappTemplate
        .replace(/\{name\}/g, student.name || 'Student')
        .replace(/\{date\}/g, selectedDate);
      const url = `https://wa.me/91${mobile}?text=${encodeURIComponent(message)}`;

      setTimeout(() => {
        window.open(url, '_blank');
      }, index * 250);
    });

    setWhatsappDialogOpen(false);
    toast.success(`Opened WhatsApp for ${absentStudents.length} absent students.`);
  };

  const getStudentPhotoUrl = (photo) => {
    if (!photo) return '';
    if (/^data:image\//i.test(photo)) return photo;
    if (/^blob:/i.test(photo)) return photo;
    if (/^https?:\/\//i.test(photo)) return photo;
    if (photo.startsWith('/')) return `${API_ORIGIN}${photo}`;
    return `${API_ORIGIN}/${photo}`;
  };

  const renderStudentAvatar = (studentName, photoUrl, size) => (
    <Tooltip
      arrow
      placement="right"
      disableHoverListener={!photoUrl}
      title={
        photoUrl ? (
          <Box
            component="img"
            src={photoUrl}
            alt={studentName}
            sx={{
              width: 220,
              height: 220,
              objectFit: 'cover',
              borderRadius: 1,
              display: 'block',
            }}
          />
        ) : (
          'No photo'
        )
      }
    >
      <Avatar
        src={photoUrl}
        alt={studentName}
        sx={{
          width: size,
          height: size,
          cursor: photoUrl ? 'zoom-in' : 'default',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': photoUrl
            ? {
                transform: 'scale(1.12)',
                boxShadow: 3,
              }
            : undefined,
        }}
      >
        {studentName?.charAt(0)?.toUpperCase() || 'S'}
      </Avatar>
    </Tooltip>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const dynamicSummary = getDynamicSummary();
  const filteredStats = getFilteredStats();
  const absentWhatsappCount = getAbsentStudentsForWhatsapp().length;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Digital Attendance
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {formatDate(selectedDate)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                type="date"
                label="Select Date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1 }}
                inputProps={{ style: { color: 'white' } }}
              />
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={loadAttendance}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={saveAttendance}
                disabled={saving || !hasChanges}
                sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }}
              >
                {saving ? 'Saving...' : 'Save Attendance'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {dynamicSummary.present}
                </Typography>
                <Typography variant="body2">Present</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {dynamicSummary.absent}
                </Typography>
                <Typography variant="body2">Absent</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {dynamicSummary.late}
                </Typography>
                <Typography variant="body2">Late</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h4" fontWeight="bold">
                  {dynamicSummary.total}
                </Typography>
                <Typography variant="body2">Total Students</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Attendance Table */}
      <Card>
        <CardContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search Student"
              placeholder="Name, Phone, Student ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 260 } }}
            />
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
              <InputLabel>Course</InputLabel>
              <Select
                value={courseFilter}
                label="Course"
                onChange={(e) => setCourseFilter(e.target.value)}
              >
                <MenuItem value="all">All Courses</MenuItem>
                {courseOptions.map((course) => (
                  <MenuItem key={course.course_code} value={course.course_code}>
                    {course.course_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
              <InputLabel>Attendance</InputLabel>
              <Select
                value={attendanceStatusFilter}
                label="Attendance"
                onChange={(e) => setAttendanceStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="present">Present</MenuItem>
                <MenuItem value="absent">Absent</MenuItem>
                <MenuItem value="late">Late</MenuItem>
                <MenuItem value="not_marked">Not Marked</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 190 } }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortBy}
                label="Sort"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="name_asc">Name A-Z</MenuItem>
                <MenuItem value="name_desc">Name Z-A</MenuItem>
                <MenuItem value="recently_joined">Recently Joined</MenuItem>
                <MenuItem value="oldest_joined">Oldest Joined</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Joined From"
              value={joinFrom}
              onChange={(e) => setJoinFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Joined To"
              value={joinTo}
              onChange={(e) => setJoinTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant={dueOnly ? 'contained' : 'outlined'}
              color={dueOnly ? 'error' : 'primary'}
              onClick={() => setDueOnly((prev) => !prev)}
            >
              Due List
            </Button>
            <Button variant="text" onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              onClick={() => setWhatsappDialogOpen(true)}
              disabled={absentWhatsappCount === 0}
            >
              WhatsApp Absent ({absentWhatsappCount})
            </Button>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Filtered Students: ${filteredStats.filteredTotal}`} color="primary" />
            <Chip label={`Present: ${filteredStats.filteredPresent}`} color="success" />
            <Chip label={`Absent: ${filteredStats.filteredAbsent}`} color="error" />
            <Chip label={`Late: ${filteredStats.filteredLate}`} color="warning" />
            <Chip label={`Not Marked: ${filteredStats.filteredNotMarked}`} color="default" />
            <Chip label={`Due Students: ${filteredStats.filteredDue}`} color="secondary" />
            {(joinFrom || joinTo) && (
              <Chip
                label={`Joined ${joinFrom || 'Any'} to ${joinTo || 'Any'}: ${filteredStats.filteredJoinedRangeCount}`}
                variant="outlined"
              />
            )}
            {courseFilter !== 'all' && (
              <Chip label={`${getCourseLabel()} Count: ${filteredStats.filteredTotal}`} variant="outlined" />
            )}
            <Chip label={`WhatsApp Reachable: ${filteredStats.filteredWhatsappReachable}`} variant="outlined" />
            <Chip label={`Course: ${getCourseLabel()}`} variant="outlined" />
          </Box>

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              Student Attendance List
            </Typography>
            {hasChanges && (
              <Chip
                label="Unsaved Changes"
                color="warning"
                size="small"
              />
            )}
          </Box>
          
          {isMobile ? (
            <Stack spacing={2}>
              {attendanceData.map((student) => {
                const present = isPresent(student.studentId);
                const checkInTime = getCurrentValue(student.studentId, 'checkInTime');
                const checkOutTime = getCurrentValue(student.studentId, 'checkOutTime');
                const status = getCurrentValue(student.studentId, 'status');
                const remarks = getCurrentValue(student.studentId, 'remarks');
                const photoUrl = getStudentPhotoUrl(student.photo || student.photo_url);
                const rowStyles = getStatusRowStyles(status);

                return (
                  <Paper
                    key={student.studentId}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderColor: rowStyles.borderColor,
                      bgcolor: rowStyles.bg === 'transparent' ? 'background.paper' : rowStyles.bg,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        {renderStudentAvatar(student.name, photoUrl, 52)}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight="bold" noWrap>
                            {student.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {student.studentIdNumber}
                          </Typography>
                        </Box>
                      </Box>
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            checked={present}
                            onChange={(e) => handlePresentCheckboxChange(student.studentId, e.target.checked)}
                            color="success"
                          />
                        }
                        label="Present"
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr 1fr' }}>
                      <TextField
                        type="time"
                        size="small"
                        label="In"
                        value={checkInTime}
                        onChange={(e) => handleCheckInTimeChange(student.studentId, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!present}
                        fullWidth
                      />
                      <TextField
                        type="time"
                        size="small"
                        label="Out"
                        value={checkOutTime}
                        onChange={(e) => handleCheckOutTimeChange(student.studentId, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={!present}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ mt: 1.5, display: 'grid', gap: 1.5 }}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={status}
                          onChange={(e) => handleStatusChange(student.studentId, e.target.value)}
                          displayEmpty
                          label="Status"
                        >
                          <MenuItem value="">
                            <em>Not Marked</em>
                          </MenuItem>
                          <MenuItem value="present">Present</MenuItem>
                          <MenuItem value="absent">Absent</MenuItem>
                          <MenuItem value="late">Late</MenuItem>
                          <MenuItem value="excused">Excused</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        placeholder="Remarks"
                        value={remarks}
                        onChange={(e) => handleRemarksChange(student.studentId, e.target.value)}
                        fullWidth
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Present
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 180 }}>
                      Student
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Student ID
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Check-In Time
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Check-Out Time
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>
                      Remarks
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.map((student) => {
                    const present = isPresent(student.studentId);
                    const checkInTime = getCurrentValue(student.studentId, 'checkInTime');
                    const checkOutTime = getCurrentValue(student.studentId, 'checkOutTime');
                    const status = getCurrentValue(student.studentId, 'status');
                    const remarks = getCurrentValue(student.studentId, 'remarks');
                    const photoUrl = getStudentPhotoUrl(student.photo || student.photo_url);
                    const rowStyles = getStatusRowStyles(status);

                    return (
                      <TableRow
                        key={student.studentId}
                        sx={{
                          bgcolor: rowStyles.bg,
                          '&:hover': { bgcolor: rowStyles.hover },
                        }}
                      >
                        <TableCell>
                          <Checkbox
                            checked={present}
                            onChange={(e) => handlePresentCheckboxChange(student.studentId, e.target.checked)}
                            color="success"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {renderStudentAvatar(student.name, photoUrl, 48)}
                            <Typography variant="body2">{student.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {student.studentIdNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={checkInTime}
                            onChange={(e) => handleCheckInTimeChange(student.studentId, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                            disabled={!present}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={checkOutTime}
                            onChange={(e) => handleCheckOutTimeChange(student.studentId, e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 140 }}
                            disabled={!present}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={status}
                              onChange={(e) => handleStatusChange(student.studentId, e.target.value)}
                              sx={{ fontSize: '0.875rem' }}
                              displayEmpty
                            >
                              <MenuItem value="">
                                <em>Not Marked</em>
                              </MenuItem>
                              <MenuItem value="present">Present</MenuItem>
                              <MenuItem value="absent">Absent</MenuItem>
                              <MenuItem value="late">Late</MenuItem>
                              <MenuItem value="excused">Excused</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            placeholder="Remarks"
                            value={remarks}
                            onChange={(e) => handleRemarksChange(student.studentId, e.target.value)}
                            sx={{ width: 170 }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {attendanceData.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No students found. Please add students first.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={whatsappDialogOpen} onClose={() => setWhatsappDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>WhatsApp Message - Absent Students</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Messages will be prepared for <strong>{absentWhatsappCount}</strong> absent students from the current filtered list.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Placeholders: {'{name}'}, {'{date}'}
          </Typography>
          <TextField
            multiline
            minRows={7}
            fullWidth
            label="Message Template"
            value={whatsappTemplate}
            onChange={(e) => setWhatsappTemplate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWhatsappDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={sendAbsentWhatsappMessages} disabled={absentWhatsappCount === 0}>
            Send via WhatsApp
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAttendance;

